// NerdGames — family registry.
//
// V44 introduces game families: instead of a flat /games/ grid of all games,
// games live under /games/<family>/ subfolders with family-specific subgrid
// pages. Adding a new family (music, english, etc.) means adding an entry
// to FAMILIES below + creating a corresponding *Home.jsx subgrid page;
// every other piece of UI that lists families iterates over this registry.
//
// Accent colors picked to leave room for future families:
//   math: cyan/teal (existing MATH_ACCENT)
//   chem: violet (existing CHEM master)
//   bio: green (existing BIO master)
//   phys: rose (existing PHYS master)
//   music: magenta #D946EF (shipped V63.2 — see music family below)
//   history: aged brass #C9A227 (shipped V63.1 — see history family below)
//
// Reserved for future families (don't reuse these hues for now):
//   orange → english / literature
//   red → art / visual
//
// V64.3 — earlier comments reserved yellow/gold for music and blue for
// history. Those reservations were never adopted; music shipped
// magenta to match its MusicShell, and history shipped brass to match
// HistoryShell. Hues updated above to reflect what's actually live.

export const FAMILIES = {
  math: {
    id: 'math',
    name: 'NerdMath',
    short: 'Math',
    routePrefix: '/games/math',
    homePath: '/games/math',
    accent: '#22D3EE',          // cyan-teal, matches MATH_ACCENT
    accentDeep: '#0E7490',
    description: 'math games — fluency through play, no flash cards',
    glyphKey: 'math',
  },
  chem: {
    id: 'chem',
    name: 'NerdChem Games',
    short: 'Chem',
    routePrefix: '/games/chem',
    homePath: '/games/chem',
    accent: '#A78BFA',          // violet
    accentDeep: '#7C3AED',
    description: 'chemistry games — periodic table, atoms, molecules',
    glyphKey: 'chem',
  },
  bio: {
    id: 'bio',
    name: 'NerdBio Games',
    short: 'Bio',
    routePrefix: '/games/bio',
    homePath: '/games/bio',
    accent: '#5DCAA5',          // green
    accentDeep: '#10B981',
    description: 'biology games — genetics, anatomy, evolution',
    glyphKey: 'bio',
  },
  phys: {
    id: 'phys',
    name: 'NerdPhys Games',
    short: 'Phys',
    routePrefix: '/games/phys',
    homePath: '/games/phys',
    accent: '#EC4899',          // rose
    accentDeep: '#BE185D',
    description: 'physics games — particles, forces, the Standard Model',
    glyphKey: 'phys',
  },
  // V63.1 — NerdHistory games. Accent matches HistoryShell (#C9A227,
  // aged brass). The "blue → history" reservation in earlier comments
  // was never adopted; HistoryShell shipped with gold and the
  // history family follows it for cohesion.
  history: {
    id: 'history',
    name: 'NerdHistory Games',
    short: 'History',
    routePrefix: '/games/history',
    homePath: '/games/history',
    accent: '#C9A227',          // aged brass / gold
    accentDeep: '#7C3AED',      // family violet (matches HistoryShell)
    description: 'history games — empires, decisions, consequences',
    glyphKey: 'history',
  },
  // V63.2 — NerdMusic Games. Accent matches MusicShell (#D946EF, the
  // music-suite magenta locked to the icon gradient start hue).
  music: {
    id: 'music',
    name: 'NerdMusic Games',
    short: 'Music',
    routePrefix: '/games/music',
    homePath: '/games/music',
    accent: '#D946EF',          // music suite magenta
    accentDeep: '#A21CAF',      // deeper magenta
    description: 'music games — theory in motion, consequence in sound',
    glyphKey: 'music',
  },
};

// Lookup helper used by routes/components
export function getFamily(id) {
  return FAMILIES[id] || null;
}

// All families as an ordered array (display order for the GamesHome grid).
export const FAMILY_LIST = [
  FAMILIES.math,
  FAMILIES.phys,
  FAMILIES.chem,
  FAMILIES.bio,
  FAMILIES.history,
  FAMILIES.music,
];
